import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { competitiveExamsApi, errorBookApi, topicProgressApi } from '../lib/api';
import { getExamPalette } from '../lib/examThemes';
import { toast } from 'sonner';
import { FontSizeSlider, useExamFontScale } from '../components/exam/FontSizeSlider';
import {
  ArrowLeft, Sun, Moon, Sliders, Flame, Target,
  Loader2, CheckCircle2, XCircle, Sparkles, Grid3x3, Play,
  Trophy, FileText, Clock, Brain, TrendingUp, TrendingDown, BookOpen,
} from 'lucide-react';

const API_LAB = process.env.REACT_APP_BACKEND_URL;

// Combined page: Weightage heat-map + Custom Test Builder.
// Entry: /exam-lab?examId=jee_main
export default function ExamLab() {
  const [fontScale, setFontScale] = useExamFontScale();
  const [params] = useSearchParams();
  const examIdParam = params.get('examId') || 'jee_main';
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState(examIdParam);
  const [weightage, setWeightage] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 40.13 — exam-specific theme skin.
  const palette = getExamPalette(examId);

  // Custom builder state
  const [subject, setSubject] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [difficulty, setDifficulty] = useState('medium');
  const [count, setCount] = useState(15);
  const [durationMin, setDurationMin] = useState(20); // Phase 40.8 — timer
  const [year, setYear] = useState('');
  const [generating, setGenerating] = useState(false);
  const [test, setTest] = useState(null);

  // Test-taking state
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);           // remaining seconds
  const [startedAt, setStartedAt] = useState(null);      // ms epoch when generated
  const timerRef = useRef(null);

  // AI analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [miniQuizLoading, setMiniQuizLoading] = useState(null); // topic being launched

  // Retry Coach — one-tap 5Q mini-quiz for a weak topic. Reuses the same
  // generate-custom endpoint so the fallback bank + LLM path both apply.
  const launchMiniQuiz = async (topic) => {
    if (!analysis) return;
    setMiniQuizLoading(topic);
    try {
      const res = await competitiveExamsApi.generateCustom(examId, {
        subject: analysis.subject || subject,
        topics: [topic],
        difficulty: 'medium',
        count: 5,
        year: null,
      });
      setSubject(res.subject);
      setSelectedTopics([topic]);
      setDifficulty('medium');
      setCount(5);
      setDurationMin(8);
      setTest(res);
      setAnswers({});
      setSubmitted(false);
      setAnalysis(null);
      setTimeLeft(8 * 60);
      setStartedAt(Date.now());
      toast.success(`5-question ${topic} mini-quiz ready — good luck!`);
      // Scroll to top so player is visible
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.message || 'Could not build mini-quiz');
    }
    setMiniQuizLoading(null);
  };

  useEffect(() => { competitiveExamsApi.getAll().then(setExams).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const w = await competitiveExamsApi.getWeightage(examId);
      setWeightage(w);
      setSubject(Object.keys(w.subjects)[0] || '');
      setSelectedTopics([]);
    } catch {
      setWeightage(null);
      toast.error('No historical data for this exam yet');
    }
    setLoading(false);
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]);
  };

  const generate = async () => {
    if (!subject) return toast.error('Pick a subject');
    setGenerating(true);
    try {
      const res = await competitiveExamsApi.generateCustom(examId, {
        subject,
        topics: selectedTopics,
        difficulty,
        count: Number(count),
        year: year ? Number(year) : null,
      });
      setTest(res);
      setAnswers({});
      setSubmitted(false);
      setAnalysis(null);
      // Start timer
      const seconds = Math.max(60, Number(durationMin) * 60);
      setTimeLeft(seconds);
      setStartedAt(Date.now());
    } catch (e) {
      toast.error(e?.message || 'Failed to generate test');
    }
    setGenerating(false);
  };

  const submitTest = useCallback(async () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setSubmitted(true);
    if (!test) return;
    test.questions.forEach((q, i) => {
      const pick = answers[i];
      if (pick !== undefined && pick !== q.correct) {
        errorBookApi.log({
          exam_id: examId,
          subject: test.subject,
          topic: q.topic || 'Custom',
          question: q.question,
          options: q.options || [],
          user_answer: q.options?.[pick],
          correct_answer: q.options?.[q.correct],
          explanation: q.explanation,
          source: 'custom',
        });
      }
      if (q.topic) {
        topicProgressApi.mark({ exam_id: examId, subject: test.subject, topic: q.topic, status: 'practiced' });
      }
    });

    // Kick off AI adaptive analysis — consumes shared LLM quota (10/day)
    const timeTaken = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : 0;
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_LAB}/api/competitive-exams/${examId}/adaptive-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          subject: test.subject,
          difficulty: test.difficulty,
          time_taken_seconds: timeTaken,
          questions: test.questions.map((q, i) => ({
            question: q.question,
            options: q.options || [],
            correct: q.correct,
            topic: q.topic,
            explanation: q.explanation,
            user_answer_idx: answers[i] === undefined ? null : answers[i],
          })),
        }),
      });
      if (r.status === 429) {
        toast.error('Daily AI quota reached. Analysis skipped — you can still review answers.');
      } else if (r.ok) {
        setAnalysis(await r.json());
      } else {
        toast.error('Could not run AI analysis — showing your score only.');
      }
    } catch {
      toast.error('Network error while running AI analysis.');
    }
    setAnalyzing(false);
  }, [test, answers, examId, startedAt]);

  // Timer countdown — auto-submits at 0
  useEffect(() => {
    if (!test || submitted) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          submitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [test, submitted, submitTest]);

  const score = test
    ? test.questions.reduce((s, q, i) => s + (answers[i] === q.correct ? 1 : 0), 0)
    : 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className={`border-b border-border bg-gradient-to-r ${palette.heroBg} backdrop-blur sticky top-0 z-10`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/competitive-exams?examId=${examId}`)} data-testid="lab-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Sliders className={`w-5 h-5 ${palette.accent}`} />
                <span className={`bg-gradient-to-r ${palette.heroFrom} ${palette.heroTo} bg-clip-text text-transparent`}>Exam Lab</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${palette.chip}`}>{palette.label}</span>
              </h1>
              <p className="text-xs text-muted-foreground">Weightage insights + build your own targeted test.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/mock-test?examId=${examId}`)} data-testid="lab-take-mock">
              <FileText className="w-4 h-4 mr-1.5" />Full NTA Mock
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6" style={{ zoom: fontScale }}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3" data-testid="lab-exam-selector">
            <span className="text-sm font-semibold">Exam:</span>
            <Select value={examId} onValueChange={setExamId}>
              <SelectTrigger className="w-64" data-testid="lab-exam-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {exams.map(e => <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <FontSizeSlider scale={fontScale} onChange={setFontScale} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : !weightage ? (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="lab-empty">
            <p>No historical data for this exam yet.</p>
          </div>
        ) : (
          <>
            {/* Weightage Heat-Map */}
            <div className="p-5 rounded-2xl border border-border bg-card" data-testid="lab-weightage">
              <h2 className="font-bold mb-1 flex items-center gap-2">
                <Grid3x3 className="w-4 h-4 text-indigo-500" />Topic Weightage — Last {weightage.years_analyzed} years ({weightage.year_range})
              </h2>
              <p className="text-xs text-muted-foreground mb-4">Click any topic to add to your custom test.</p>
              <div className="space-y-4">
                {Object.entries(weightage.subjects).map(([sub, topics]) => (
                  <div key={sub} data-testid={`weightage-subject-${sub.replace(/\s+/g, '-')}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-sm">{sub}</p>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setSubject(sub); setSelectedTopics(topics.filter(t => t.weightage_pct >= 50).slice(0, 5).map(t => t.topic)); }}
                        data-testid={`pick-high-freq-${sub.replace(/\s+/g, '-')}`}
                      >
                        <Flame className="w-3.5 h-3.5 mr-1 text-orange-500" />Pick high-frequency topics
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {topics.map(t => {
                        const isPicked = subject === sub && selectedTopics.includes(t.topic);
                        return (
                          <button
                            key={t.topic}
                            onClick={() => { setSubject(sub); toggleTopic(t.topic); }}
                            data-testid={`weightage-topic-${sub.replace(/\s+/g, '-')}-${t.topic.replace(/\s+/g, '-')}`}
                            className={`group px-2 py-1.5 rounded-md text-[11px] font-medium transition border ${isPicked ? 'bg-indigo-500 text-white border-indigo-600' : t.weightage_pct >= 80 ? 'border-rose-400 bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800' : t.weightage_pct >= 50 ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800' : t.weightage_pct >= 30 ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800' : 'border-border text-muted-foreground hover:border-muted-foreground/40'}`}
                            title={`${t.year_count} of ${weightage.years_analyzed} years · ${t.years.join(', ')}`}
                          >
                            {t.topic}
                            <span className="ml-1 opacity-70">{t.weightage_pct}%</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-[10px] pt-3 border-t border-border/60">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-rose-400 rounded-sm" />Very High (≥80%)</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-orange-400 rounded-sm" />High (≥50%)</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-400 rounded-sm" />Medium (≥30%)</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-muted rounded-sm border" />Low (&lt;30%)</span>
              </div>
            </div>

            {/* Custom Test Builder */}
            <div className="p-5 rounded-2xl border border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 dark:to-violet-950/20" data-testid="lab-builder">
              <h2 className="font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-500" />Build a Custom Test
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold block mb-1.5">Subject</label>
                  <Select value={subject} onValueChange={(v) => { setSubject(v); setSelectedTopics([]); }}>
                    <SelectTrigger data-testid="builder-subject"><SelectValue placeholder="Pick subject" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(weightage.subjects).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1.5">Difficulty</label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger data-testid="builder-difficulty"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                      <SelectItem value="mixed">Mixed (Easy/Med/Hard)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1.5">Number of questions (5-40)</label>
                  <input
                    type="number" min="5" max="40" value={count} onChange={(e) => setCount(e.target.value)}
                    data-testid="builder-count"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1.5">Duration in minutes (5-180)</label>
                  <input
                    type="number" min="5" max="180" value={durationMin}
                    onChange={(e) => setDurationMin(e.target.value)}
                    data-testid="builder-duration"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1.5">Past-year style (optional)</label>
                  <Select value={year || 'none'} onValueChange={(v) => setYear(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="builder-year"><SelectValue placeholder="Not required" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not required</SelectItem>
                      {[2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedTopics.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-white/60 dark:bg-card/60 border border-indigo-200 dark:border-indigo-900" data-testid="builder-selected-topics">
                  <p className="text-xs font-semibold mb-1.5">Selected topics ({selectedTopics.length}):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTopics.map(t => (
                      <Badge key={t} className="bg-indigo-500 text-white cursor-pointer" onClick={() => toggleTopic(t)}>
                        {t} ×
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={generate}
                disabled={generating || !subject}
                className="mt-4 w-full bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
                data-testid="builder-generate"
              >
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : <><Play className="w-4 h-4 mr-2" />Generate & Start Test</>}
              </Button>
            </div>

            {/* Test player */}
            {test && (
              <div className="p-5 rounded-2xl border border-border bg-card" data-testid="builder-test">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <div>
                    <h3 className="font-bold">{test.subject} · {test.difficulty}</h3>
                    <p className="text-xs text-muted-foreground">{test.count} questions</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!submitted && (
                      <Badge
                        className={`text-sm font-mono ${timeLeft <= 60 ? 'bg-rose-500 animate-pulse' : timeLeft <= 300 ? 'bg-amber-500' : 'bg-indigo-500'} text-white`}
                        data-testid="exam-lab-timer"
                      >
                        <Clock className="w-3.5 h-3.5 mr-1" />
                        {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
                      </Badge>
                    )}
                    {submitted && (
                      <Badge className="bg-emerald-500 text-white" data-testid="builder-score">
                        <Trophy className="w-3.5 h-3.5 mr-1" />{score}/{test.count}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  {test.questions.map((q, i) => (
                    <div key={i} className="p-4 rounded-xl border border-border" data-testid={`builder-q-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-[10px]">Q{i + 1}</Badge>
                        {q.topic && <Badge variant="outline" className="text-[10px]">{q.topic}</Badge>}
                        {q.difficulty && <Badge variant="outline" className="text-[10px]">{q.difficulty}</Badge>}
                      </div>
                      <p className="text-sm font-medium mb-3">{q.question}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {q.options?.map((opt, oi) => {
                          const picked = answers[i] === oi;
                          const correct = oi === q.correct;
                          let cls = 'border-border';
                          if (submitted && correct) cls = 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30';
                          else if (submitted && picked && !correct) cls = 'border-rose-500 bg-rose-50 dark:bg-rose-950/30';
                          else if (picked) cls = 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30';
                          return (
                            <button
                              key={oi}
                              onClick={() => !submitted && setAnswers(a => ({ ...a, [i]: oi }))}
                              disabled={submitted}
                              data-testid={`builder-q-${i}-opt-${oi}`}
                              className={`text-left px-3 py-2 rounded-lg border-2 text-xs transition ${cls}`}
                            >
                              <span className="font-bold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                              {submitted && correct && <CheckCircle2 className="inline-block w-3.5 h-3.5 ml-2 text-emerald-500" />}
                              {submitted && picked && !correct && <XCircle className="inline-block w-3.5 h-3.5 ml-2 text-rose-500" />}
                            </button>
                          );
                        })}
                      </div>
                      {submitted && q.explanation && (
                        <p className="mt-2 p-2 rounded bg-sky-50 dark:bg-sky-950/30 text-xs text-sky-900 dark:text-sky-200"><b>Why:</b> {q.explanation}</p>
                      )}
                    </div>
                  ))}
                </div>
                {!submitted ? (
                  <Button className="mt-4 w-full" onClick={submitTest} data-testid="builder-submit">Submit & See Results</Button>
                ) : (
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => { setTest(null); setAnswers({}); setSubmitted(false); setAnalysis(null); setTimeLeft(0); }} data-testid="builder-new">
                      <Sparkles className="w-4 h-4 mr-1.5" />Build another
                    </Button>
                    <Button className="flex-1" onClick={() => navigate('/error-book')} data-testid="builder-errorbook">
                      <Target className="w-4 h-4 mr-1.5" />Review errors
                    </Button>
                  </div>
                )}

                {/* Phase 40.8 — AI Adaptive Analysis */}
                {submitted && (
                  <div className="mt-6 p-4 rounded-2xl border-2 border-violet-300 dark:border-violet-800 bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-950/30 dark:to-fuchsia-950/30" data-testid="ai-analysis-panel">
                    <h4 className="font-bold flex items-center gap-2 mb-3">
                      <Brain className="w-4 h-4 text-violet-500" />
                      AI Adaptive Analysis
                      <Badge variant="outline" className="text-[10px]">consumes 1/10 daily AI</Badge>
                    </h4>
                    {analyzing ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-analysis-loading">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Analysing your performance…
                      </div>
                    ) : analysis ? (
                      <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="p-2 rounded-lg bg-white/70 dark:bg-card/70 text-center">
                            <p className="text-[10px] text-muted-foreground uppercase">Accuracy</p>
                            <p className="text-xl font-black text-violet-600" data-testid="ai-accuracy">{analysis.accuracy_pct}%</p>
                          </div>
                          <div className="p-2 rounded-lg bg-white/70 dark:bg-card/70 text-center">
                            <p className="text-[10px] text-muted-foreground uppercase">Correct</p>
                            <p className="text-xl font-black text-emerald-600">{analysis.correct}/{analysis.total}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-white/70 dark:bg-card/70 text-center">
                            <p className="text-[10px] text-muted-foreground uppercase">Skipped</p>
                            <p className="text-xl font-black text-amber-600">{analysis.unanswered}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-white/70 dark:bg-card/70 text-center">
                            <p className="text-[10px] text-muted-foreground uppercase">Source</p>
                            <p className="text-xs font-bold mt-1.5">{analysis.source === 'llm' ? 'AI Coach' : 'Rule-based'}</p>
                          </div>
                        </div>

                        <div className="p-3 rounded-lg bg-white/70 dark:bg-card/70" data-testid="ai-summary">
                          <p className="text-[10px] uppercase font-bold text-violet-600 mb-1">Summary</p>
                          <p>{analysis.ai?.summary}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                            <p className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400 mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Strengths</p>
                            <ul className="list-disc list-inside space-y-0.5 text-xs">
                              {(analysis.ai?.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                          <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800">
                            <p className="text-[10px] uppercase font-bold text-rose-700 dark:text-rose-400 mb-1 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Weaknesses</p>
                            <ul className="list-disc list-inside space-y-0.5 text-xs">
                              {(analysis.ai?.weaknesses || []).map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        </div>

                        {/* Retry Coach — 1-tap mini-quiz per weak topic */}
                        {(analysis.weak_topics || []).length > 0 && (
                          <div className="p-3 rounded-lg bg-fuchsia-50 dark:bg-fuchsia-950/30 border border-fuchsia-200 dark:border-fuchsia-800" data-testid="retry-coach-panel">
                            <p className="text-[10px] uppercase font-bold text-fuchsia-700 dark:text-fuchsia-400 mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" />Retry Coach — 5-question mini-quizzes</p>
                            <div className="flex flex-wrap gap-2">
                              {(analysis.weak_topics || []).slice(0, 5).map((topic, i) => (
                                <button
                                  key={i}
                                  onClick={() => launchMiniQuiz(topic)}
                                  disabled={miniQuizLoading === topic}
                                  className="text-[11px] px-3 py-1.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-60 text-white font-semibold shadow-sm transition-colors flex items-center gap-1"
                                  data-testid={`retry-coach-${i}`}
                                  title={`Practice 5 more ${topic} questions right now`}
                                >
                                  {miniQuizLoading === topic ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                                  Practise {topic} × 5
                                </button>
                              ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-2">Tapping a topic instantly builds a fresh 5-question quiz — great for turning a weakness into a win.</p>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800">
                            <p className="text-[10px] uppercase font-bold text-indigo-700 dark:text-indigo-400 mb-1">Next steps</p>
                            <ul className="list-disc list-inside space-y-0.5 text-xs">
                              {(analysis.ai?.next_steps || []).map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                          <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800">
                            <p className="text-[10px] uppercase font-bold text-sky-700 dark:text-sky-400 mb-1 flex items-center gap-1"><BookOpen className="w-3 h-3" />Recommended chapters</p>
                            <ul className="list-disc list-inside space-y-0.5 text-xs">
                              {(analysis.ai?.recommended_chapters || []).map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        </div>

                        {analysis.ai?.target_score && (
                          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex items-center gap-2 text-sm" data-testid="ai-target">
                            <Target className="w-4 h-4 text-amber-600" />
                            <span><b>Target for next attempt:</b> {analysis.ai.target_score}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">AI analysis unavailable for this attempt.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
